import { Action, SerializedError, Store } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { DEFAULT_PAGE_TITLE, PREFERENCES, TITLE_SEPARATOR } from '@/core/utils/app.constants';
import { PORTAL_ROLES } from '@/authorization/authorization.constants';
import { DASHBOARD_REFRESH_TIMER } from '@/dashboard/utils/dashboard.utils';
import { ListItemContainerProps } from '@/components/ListWithPanel/ListItem/ListItemContainer';
import {
  EMPLOYEE_DISPLAY_LABELS_BY_STATUS,
  FilterParamsType,
  FilterType,
  ValidEmployeeStatusLabels,
  ValidEmployeeStatuses,
  contributionColorNames,
} from '@/common/common.types';
import webStorage, { LOCAL_STORAGE_KEYS } from '@/core/web-storage/webStorage';
import { isSandboxBoard } from '@/authorization/utils/authorization.utils';
import { RootStateType } from '@/core/store/store';
import { COMMON_STORE_KEY } from '@/core/store/commonReducer';
import { getUserPortalRole } from '@/core/web-storage/webStorage.utils';

export type TimerType = ReturnType<typeof setTimeout>;
const ABORT_ERROR = 'AbortError';
export const EMPTY_PLACEHOLDER = '--';
export const TERMINATION_DATE_EMPTY_PLACEHOLDER = 'None';
export const monthMapper: Record<string, string> = {
  '01': 'January',
  '02': 'February',
  '03': 'March',
  '04': 'April',
  '05': 'May',
  '06': 'June',
  '07': 'July',
  '08': 'August',
  '09': 'September',
  '10': 'October',
  '11': 'November',
  '12': 'December',
};

function buildPageTitle(title: string) {
  return `${title} ${TITLE_SEPARATOR} ${DEFAULT_PAGE_TITLE}`;
}

function reduceTokenString(main: string, token: string) {
  return main.replace('{n}', token);
}

export function reduceMultipleTokenString(main: string, tokens: string[]) {
  let index = 0;
  return main.replace(/{n}/g, () => tokens[index++] || '{n}');
}

function formatCurrency(amount: number | string) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const filtered = Number(amount);
  return formatter.format(filtered);
}

export function formatCurrencyWithoutSign(amount: number) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const filtered = Number(amount);
  const formatted = formatter
    .formatToParts(filtered)
    .map((p) => (p.type == 'currency' ? '' : p.value))
    .join('');

  return formatted;
}

export function formatCurrencyToNumber(currency: string) {
  const number = Number(currency.replace(/[^0-9.-]+/g, ''));
  return isNaN(number) ? 0 : number;
}

export function getFormattedCurrency(value: number) {
  const filtered = Object.is(value, -0) ? 0 : value;

  return formatCurrency(filtered);
}

function formatSIN(target: string) {
  const sin = target.toString();
  const length = sin.length ?? 0;
  if (length < 9) {
    return sin;
  }
  const onlyDigits = sin.replace(/^D/, '');
  return (
    onlyDigits.substring(0, 3) + ' ' + onlyDigits.substring(3, 6) + ' ' + onlyDigits.substring(6, 9)
  );
}

function formatNumberToLocale(num: string | number) {
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateTommdd(date: string) {
  if (!isValidDate(date)) {
    return date;
  } else {
    const instance = new Date(date);
    return instance.toLocaleDateString('en-GB');
  }
}

export function formatDateByLocale(date: string, _locale = 'en-CA') {
  if (!isValidDate(date)) {
    return undefined;
  } else {
    return date.split('T')[0];
  }
}

/**
 * @param isoDate 2023-09-01T00:00:00
 */
function isValidDate(isoDate: string) {
  let rawDate = isoDate;
  if (rawDate.slice(-1) !== 'Z') {
    rawDate += 'Z';
  }

  if (isNaN(Date.parse(rawDate))) {
    return false;
  }

  const date = new Date(rawDate);
  const rawLeft = rawDate.split('T')[0];
  const parsedLeft = date.toISOString().split('T')[0];
  if (rawLeft !== parsedLeft) {
    return false;
  }

  return true;
}

function getSafeDashboardTimer(time: number) {
  const MIN = 1000 * 60;
  const MAX = 1000 * 60 * 10;

  if (time < MIN) {
    return MIN;
  } else if (time > MAX) {
    return MAX;
  }

  return time;
}

function getDashboardRefreshTimer() {
  const storageTimer = webStorage.local.getItem(LOCAL_STORAGE_KEYS.REFRESH_TIME);

  if (storageTimer) {
    return getSafeDashboardTimer(Number(storageTimer));
  }

  return DASHBOARD_REFRESH_TIMER;
}

function getDashboardRefreshTimeByEnv() {
  if (ENVIRONMENT_NAME === 'staging') {
    return DASHBOARD_REFRESH_TIMER;
  }
  return getDashboardRefreshTimer();
}

function pluralize(text: string, num: number, appendor = 's') {
  if (num > 1) {
    return text + appendor;
  }

  return text;
}

export function isAbortError<T extends FetchBaseQueryError | SerializedError | string | undefined>(
  error: T,
) {
  return error && typeof error !== 'string' && 'name' in error && error.name === ABORT_ERROR;
}

export function extractDBdateString(dbDate: string) {
  const year = Number(dbDate.substring(0, 4));
  const month = Number(dbDate.substring(4));

  return [year, month];
}

export function jsDateToDbDate(date: Date) {
  return date.getFullYear() + (date.getMonth() + 1).toString().padStart(2, '0');
}

export function dbDateToJsDate(date: string) {
  const [year, month] = extractDBdateString(date);

  return new Date(year, month - 1);
}

export function getTotalFromContentRange(contentRange: string): number | null {
  const regex = /\/(\d+)/;
  const match = contentRange.match(regex);

  if (match && match[1]) {
    return parseInt(match[1], 10);
  } else {
    return null;
  }
}

export function fullNameBuilder(firstName: string, lastName: string) {
  const upperFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  return `${upperFirst(String(lastName))}, ${upperFirst(String(firstName))}`;
}

export function fullNameInLocale(firstName: string, lastName: string) {
  const upperFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  return upperFirst(String(firstName)) + ' ' + upperFirst(String(lastName));
}

export function computeItemPositionFromList(
  index: number,
  total: number,
): ListItemContainerProps['type'] {
  if (total > 1) {
    if (index === 0) {
      return 'top';
    }
    if (index > 0 && index < total - 1) {
      return 'middle';
    }
    if (index == total - 1) {
      return 'bottom';
    }
  }

  return 'bottom';
}

export function buildStatusTipsFromContributionColors(colorName: typeof contributionColorNames): {
  [k in keyof typeof contributionColorNames]: string;
} {
  return {
    Accepted: `colour ${colorName.Accepted}, status accepted.`,
    Errors: `colour ${colorName.Errors}, status error.`,
    Submitted: `colour ${colorName.Submitted}, status submitted.`,
    Unsubmitted: `colour ${colorName.Unsubmitted}, status unsubmitted.`,
    Warnings: `colour ${colorName.Warnings}, status warning.`,
  };
}

export function getIsNaNSafeNumber(num: string | number, replacer = 0) {
  const number = Number(num);
  if (isNaN(number)) {
    return replacer;
  }
  return number;
}

export function formatDOB(date: string) {
  if (!date) {
    return null;
  }
  const isValid = isValidDate(date);
  if (isValid) {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  return null;
}

export function formatPhoneNumberWithExtension(phoneNumber: string) {
  const targetPhone = phoneNumber?.trim()?.substring(0, 14) ?? '';
  const length = targetPhone?.length ?? 0;

  if (length < 10) {
    return phoneNumber;
  }

  const match = phoneNumber.match(/^(\d{3})(\d{3})(\d{4})(\d{0,4})(\d*)$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}${match[4] ? ` ex. ${match[4]}` : ''}`;
  }

  return phoneNumber.trim();
}

export function formatPhoneNumberWithoutExtension(phoneNumber: string) {
  const targetPhone = phoneNumber?.trim()?.substring(0, 10) ?? '';
  const length = targetPhone?.length ?? 0;

  if (length < 10) {
    return phoneNumber;
  }

  const match = targetPhone.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phoneNumber.trim();
}

export function isNotValidForDisplay(value: string | null | undefined) {
  return !value || (value && String(value).trim() === '');
}

export function formatStringToNumber(phone: string) {
  const digits = phone.replace(/[^0-9]/g, '');
  return digits;
}

export function formatPostalCode(input: string) {
  const cleaned = removeSpacesFromString(input);
  if (cleaned.length < 6) {
    return input;
  }

  return cleaned.substring(0, 3) + ' ' + cleaned.substring(3);
}

export function removeSpacesFromString(input: string) {
  return input.replace(/\s/g, '');
}

export function mergeObjects(left: Record<string, unknown>, right: Record<string, unknown>) {
  return {
    ...left,
    ...right,
  };
}

export function stringToNumber(input: string) {
  if (!input) {
    return null;
  }

  return input.replace(/[^0-9]/g, '');
}

export function updatePreferences(
  preferenceKey: string,
  preferenceValue: string | boolean | number,
) {
  const preferences = webStorage.local.getItem(PREFERENCES.ROOT);
  let newPreferences = {};
  if (preferences) {
    const preferencesObject = JSON.parse(JSON.stringify(preferences));
    newPreferences = {
      ...preferencesObject,
      [preferenceKey]: preferenceValue,
    };
  } else {
    newPreferences = {
      [preferenceKey]: preferenceValue,
    };
  }

  webStorage.local.setItem(PREFERENCES.ROOT, newPreferences);
}

export function getPreferences(
  preferenceKey: string,
  defaultValue: string | boolean | number,
): string | boolean | number {
  const preferences = webStorage.local.getItem(PREFERENCES.ROOT);
  if (preferences) {
    const preferenceObject = JSON.parse(JSON.stringify(preferences));
    if (preferenceObject.hasOwnProperty(preferenceKey)) return preferenceObject[preferenceKey];
  }
  return defaultValue;
}

export function decodeJWT(token: string) {
  const parts = token.split('.');
  const decodedPayload = JSON.parse(window.atob(parts[1]));
  return decodedPayload;
}

export function getBoardFromJWT(decodedToken: { extension_Board: string }) {
  const boardClaim = decodedToken.extension_Board;
  if (boardClaim) {
    const boardId = boardClaim.split(',')[0];
    return boardId;
  }

  return undefined;
}

export function getUserIdFromJWT(decodedToken: { oid: string }) {
  const userObjectIdClaim = decodedToken.oid;
  if (userObjectIdClaim) {
    return userObjectIdClaim;
  }

  return undefined;
}

export function getUserPortalRoleFromJWT(decodedToken: { extension_PortalRole: string }) {
  const userPortalRoleClaim = decodedToken.extension_PortalRole;
  if (userPortalRoleClaim) {
    return userPortalRoleClaim as PORTAL_ROLES;
  }

  return undefined;
}

export function getUserNameFromJWT(decodedToken: { given_name: string; family_name: string }) {
  if (decodedToken.given_name && decodedToken.family_name) {
    return `${decodedToken.given_name} ${decodedToken.family_name}`;
  } else if (decodedToken.family_name) {
    return decodedToken.family_name;
  } else if (decodedToken.given_name) {
    return decodedToken.given_name;
  } else {
    return '';
  }
}

export function getUserEmailFromJWT(decodedToken: { emails: string[] }) {
  return decodedToken?.emails?.[0];
}

export function generateFilterObject(filterOperator: string, filterParams: FilterParamsType[]) {
  const filter: Record<string, FilterType[]> = {
    [filterOperator]: [],
  };

  for (const param of filterParams) {
    filter[filterOperator].push({
      where: {
        key: param.key,
        operator: param.operator,
        value: param.value,
      },
    });
  }
  return filter;
}

export function convertEmployeeStatusToLabel(
  dbStatus: ValidEmployeeStatuses,
): ValidEmployeeStatusLabels {
  return EMPLOYEE_DISPLAY_LABELS_BY_STATUS[dbStatus] ?? EMPLOYEE_DISPLAY_LABELS_BY_STATUS[''];
}

export function removeDuplicates<T>(array: T[]) {
  return array.filter((value, key) => array.indexOf(value) === key);
}

export function getStartOfSchoolYearFromSchoolYear(year: number, toNumber?: true): number;
export function getStartOfSchoolYearFromSchoolYear(year: number, toNumber?: false): string;
export function getStartOfSchoolYearFromSchoolYear(year: number, toNumber?: boolean) {
  const startOfSchoolYear = `${year}09`;
  return toNumber ? Number(startOfSchoolYear) : startOfSchoolYear;
}
export function getYearFromStartOfSchoolYear(year: number) {
  return Number(year.toString().substring(0, 4));
}

export function getShortDateInStringFromDBDate(dbDate: string) {
  const month = dbDate.slice(-2);
  const year = dbDate.slice(0, 4);
  const dateInString = monthMapper[month] + ' ' + year;
  return dateInString;
}

export function isOrTrue(...args: boolean[]) {
  return args.some(Boolean);
}

export function getCreateBrowserRouterFutureFlags() {
  return {
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
    v7_relativeSplatPath: true,
    v7_startTransition: true,
  };
}

export function getRouterProviderFutureFlags() {
  return {
    v7_startTransition: true,
  };
}

export function isProdAndSandboxBoard() {
  return ENVIRONMENT_NAME === 'production' && isSandboxBoard();
}

export function getGlobalLocation() {
  return window.location;
}

export function globalLocationReplace(url: string) {
  window.location.replace(url);
}

export function globalLocationHref(url: string) {
  window.location.href = url;
}

export function isPeriodInCountCache(periods: string[], store: Store<unknown, Action, object>) {
  const state = store.getState() as RootStateType;

  const allCounts = state[COMMON_STORE_KEY].counts;
  const allPeriodsInCache = Object.keys(allCounts);
  const isPresent = periods.every((period) => allPeriodsInCache.includes(period));

  return isPresent;
}

export function getCountsForPeriodFromCache(period: string, store: Store<unknown, Action, object>) {
  const state = store.getState() as RootStateType;
  const allCounts = state[COMMON_STORE_KEY].counts;

  if (!allCounts[period]) {
    throw new Error(`${period} counts not in cache`);
  }

  return allCounts[period];
}

export function isATRFUser() {
  const userPortalRole = getUserPortalRole();
  return userPortalRole === PORTAL_ROLES.ATRF;
}

export {
  buildPageTitle,
  reduceTokenString,
  formatCurrency,
  formatSIN,
  formatNumberToLocale,
  formatDateTommdd,
  isValidDate,
  pluralize,
  getDashboardRefreshTimer,
  getDashboardRefreshTimeByEnv,
};
